import React, { useRef, Fragment } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { BlurText } from './animations/BlurText';
import { DecryptedText } from './animations/DecryptedText';
import { Magnet } from './animations/Magnet';
import { RadarEcho } from './animations/RadarEcho';
/**
 * Two-screen hero.
 *
 *   Screen 1 (first viewport): brand reveal — badge + logo, centered.
 *   Screen 2 (second viewport): headline, copy, and CTAs.
 *
 * Both share a pinned background (Aurora + 3D scene + vignette) so the
 * atmosphere stays continuous as the user scrolls between screens.
 *
 * No cross-fade math — each act gets its own dedicated 100vh of scroll.
 * Animations trigger on viewport entry, so they feel deterministic.
 */
export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  // The pinned background gets a very gentle parallax shift between the
  // two screens — just enough motion to feel alive, not enough to compete.
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end end']
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.8, 1], [0.45, 0.3, 0.15]);
  return <section ref={ref} className="relative w-full" data-id="element-704">
      {/* SVG colour-matrix filter — drops white in the logo image to transparent */}
      <svg width="0" height="0" className="absolute" aria-hidden="true" data-id="element-705">
        <defs data-id="element-706">
          <filter id="periscan-remove-white" colorInterpolationFilters="sRGB" data-id="element-707">
            <feColorMatrix type="matrix" values="-1 0 0 0 1
                       0 -1 0 0 1
                       0 0 -1 0 1
                       0 0 0 1 0" data-id="element-708" />
            <feColorMatrix type="hueRotate" values="180" data-id="element-709" />
            <feColorMatrix type="matrix" values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      1 1 1 0 0" data-id="element-710" />
          </filter>
        </defs>
      </svg>

      {/* ── PINNED BACKGROUND ─────────────────────────────────────────── */}
      {/* Calm, near-monochrome backdrop. No 3D orbs, no cyber-grid, no
                gradient blob. Just deep ink with a soft directional gradient so
                the type carries the page. */}
      <div className="sticky top-0 h-screen w-full overflow-hidden -mb-[100vh] z-0" data-id="element-711">
        <motion.div style={{
        opacity: bgOpacity
      }} className="absolute inset-0 pointer-events-none" aria-hidden="true" data-id="element-712">
          {/* Single low-key directional wash — top-left lift, no orbs */}
          <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse 70% 50% at 22% 18%, rgba(26,143,212,0.08) 0%, transparent 60%)'
        }} data-id="element-713" />
        </motion.div>

        {/* Continuous radar sweep — the hacker's-eye view from day one.
              Sits behind content, fades with the rest of the backdrop as the
              user scrolls to screen 2. */}
        <motion.div style={{
        opacity: bgOpacity
      }} className="absolute inset-0 pointer-events-none" aria-hidden="true" data-id="element-714">
          <RadarEcho intensity={0.95} sweepDuration={12} ringDuration={6} rings={3} data-id="element-715" />
        </motion.div>

        {/* Final ink overlay keeps the bottom edge anchored */}
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(180deg, transparent 0%, transparent 60%, #050608 95%)'
      }} data-id="element-716" />
      </div>

      {/* ── SCREEN 1: BADGE + LOGO ───────────────────────────────────── */}
      <div className="relative z-10 h-screen w-full flex flex-col items-center justify-center px-6" data-id="element-717">
        {/* REBORN 2026 badge */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="mb-10" data-id="element-718">
          <span className="inline-flex items-center gap-4 text-[#1a8fd4] text-[11px] font-mono uppercase tracking-[0.3em]" data-id="element-719">
            <span className="h-px w-10 bg-[#1a8fd4]/40" aria-hidden="true" data-id="element-720" />
            <DecryptedText text="Founded 2004 · Reborn 2026" speed={60} animateOn="mount" data-id="element-721" />
            <span className="h-px w-10 bg-[#1a8fd4]/40" aria-hidden="true" data-id="element-722" />
          </span>
        </motion.div>

        {/* Logo — original artwork, white background filtered to transparent.
            No pulsing halo, no drop-shadow glow — the logo carries itself. */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        ease: 'easeOut'
      }} className="relative flex items-center justify-center" data-id="element-723">
          <img src="https://cdn.magicpatterns.com/uploads/19wZLyvpjSe2tkaaA1WT1m/p_logo_02_(1)_(1).jpg" alt="Periscan" className="relative h-28 sm:h-32 md:h-40 lg:h-48 w-auto object-contain" style={{
          filter: 'url(#periscan-remove-white)'
        }} data-id="element-724" />
        </motion.div>

        {/* Scroll cue */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.4,
        duration: 0.8
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 pointer-events-none" data-id="element-725">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50" data-id="element-726">
            Scroll to continue
          </span>
          <motion.div animate={{
          y: [0, 8, 0],
          opacity: [0.4, 1, 0.4]
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-px h-10 bg-gradient-to-b from-[#1a8fd4] to-transparent" data-id="element-727" />
        </motion.div>
      </div>

      {/* ── SCREEN 2: HEADLINE + CTAs ─────────────────────────────────── */}
      <div className="relative z-10 h-screen w-full flex items-center px-6 lg:px-8" data-id="element-728">
        <div className="max-w-5xl mx-auto w-full text-center" data-id="element-729">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-30%'
        }} transition={{
          duration: 0.5
        }} className="mb-8 flex justify-center" data-id="element-730">
            <span className="inline-flex items-center gap-4 text-white/60 text-[11px] font-mono uppercase tracking-[0.3em]" data-id="element-731">
              <span className="h-px w-10 bg-white/15" aria-hidden="true" data-id="element-732" />
              Self-service Automated Security Validation
              <span className="h-px w-10 bg-white/15" aria-hidden="true" data-id="element-733" />
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-30%'
        }} transition={{
          duration: 0.7
        }} className="text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-semibold tracking-[-0.035em] leading-[1] text-white" data-id="element-734">
            Prove what can actually compromise you.
          </motion.h1>

          {/* Subheadline — grounds the tagline in concrete action */}
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.7,
          delay: 0.4
        }} className="mt-8 text-lg md:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed" data-id="element-735">
            Periscan validates{' '}
            <span className="text-white" data-id="element-736">
              exposure, controls, attack paths, AI applications, and fixes
            </span>{' '}
            — then turns the results into proof your team can use.
          </motion.p>

          {/* Supporting line — heritage + attacker-perspective credibility */}
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="mt-4 text-sm md:text-base text-white/55 max-w-2xl mx-auto" data-id="element-737">
            No scanner noise. No stale pentest report. No enterprise rollout
            circus.
          </motion.p>

          {/* Hero bullets */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.6,
          delay: 0.8
        }} className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-mono uppercase tracking-widest text-white/50" data-id="element-738">
            {['Validated exposure', 'Control validation', 'Attack-path validation', 'AI app validation', 'Fix verification', 'Evidence packs'].map((b, i, arr) => <Fragment key={b} data-id="element-739">
                <span className="inline-flex items-center gap-2" data-id="element-740">
                  <span className="w-1 h-1 rounded-full bg-[#1a8fd4]" data-id="element-741" />
                  {b}
                </span>
                {i < arr.length - 1 && <span className="text-white/15 hidden md:inline" data-id="element-742">·</span>}
              </Fragment>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="mt-6 font-mono text-sm text-[#1a8fd4]" data-id="element-743">
            <DecryptedText text="Find the path. Validate the risk. Prove it's fixed." speed={30} data-id="element-744" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.7,
          delay: 0.85
        }} className="mt-10 flex flex-col sm:flex-row gap-3 justify-center" data-id="element-745">
            {/* Primary — self-serve PLG entry */}
            <Magnet strength={0.2} data-id="element-746">
              <a href="/early-access" className="group relative overflow-hidden inline-flex items-center gap-3 px-6 py-3.5 bg-white text-black font-medium rounded-full text-sm hover:bg-white/90 transition-colors" data-id="element-747">
                <span className="relative z-10" data-id="element-748">Request Early Access</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 relative z-10" data-id="element-749" />
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-[#1a8fd4]/20 to-transparent" data-id="element-750" />
              </a>
            </Magnet>

            {/* Secondary — See How It Works (educational pathway) */}
            <Magnet strength={0.15} data-id="element-751">
              <a href="#validation-snapshot" className="group inline-flex items-center gap-3 px-6 py-3.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-full text-sm transition-colors backdrop-blur-sm" data-id="element-752">
                <span data-id="element-753">Start Validation Snapshot</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-754" />
              </a>
            </Magnet>
          </motion.div>

          {/* Free-assessment supporting line */}
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.6,
          delay: 1.0
        }} className="mt-5 text-xs text-white/40 max-w-md mx-auto" data-id="element-755">
            Get an initial view of your continuous validation readiness in
            minutes. No demo call required.
          </motion.p>

          {/* Tertiary text link */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          margin: '-20%'
        }} transition={{
          duration: 0.6,
          delay: 1.1
        }} className="mt-3" data-id="element-756">
            <a href="/how-it-works" className="group inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-white/50 hover:text-[#1a8fd4] transition-colors" data-id="element-757">
              See how it works
              <ArrowUpRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-758" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}